import { useUiText } from "../../locale/UiLocale";
import { useMemo } from "react";
import type { Column } from "./DataTableTypes";
import { filterableColumns, filterLabel, filterOptions, NO_FILTER } from "./filterOptions";
import { FilterPill } from "./FilterPill";
import { isNarrowed } from "./rowFilter";
import type { TableSearch } from "./useTableSearch";

interface FilterPillsProps<R> {
  columns: Column<R>[];
  rows: R[];
  search: TableSearch;
}

/** One pill per filterable column, plus "Clear" while the search or a filter narrows the rows. */
export function FilterPills<R>({ columns, rows, search }: FilterPillsProps<R>): React.JSX.Element {
  const t = useUiText();
  const filterable = useMemo(() => filterableColumns(columns), [columns]);
  const options = useMemo(() => filterOptions(filterable, rows), [filterable, rows]);
  const showClear = isNarrowed(search.filters, search.query) && filterable.length > 0;

  return (
    <>
      {filterable.map((col) => (
        <FilterPill
          key={col.key}
          label={filterLabel(col)}
          value={search.filters[col.key] ?? NO_FILTER}
          options={options[col.key] ?? []}
          onChange={(value) => search.setFilter(col.key, value)}
        />
      ))}
      {showClear ? (
        <button type="button" className="nb-link-btn" onClick={search.clear}>
          {t("Clear")}
        </button>
      ) : null}
    </>
  );
}
